import { useState } from 'react'

import { DesktopSoundboard } from '../../types/DesktopSoundboard'
import FrontendWebSoundboard from '../../types/FrontendWebSoundboard'
import MobileSoundboard from '../../types/MobileSoundboard'
import { LoadingButton } from '../../ui/Button'
import Chip from '../../ui/Chip'
import { DialogActions, DialogContent, DialogTitle } from '../../ui/Dialog'
import Paper from '../../ui/Paper'
import Tooltip from '../../ui/Tooltip'
import Typography from '../../ui/Typography'
import ContentCopyIcon from '../../ui/icons/ContentCopy'
import DataObjectIcon from '../../ui/icons/DataObject'
import FolderZipIcon from '../../ui/icons/FolderZip'
import OpenInNewIcon from '../../ui/icons/OpenInNew'
import PhotoAlbumIcon from '../../ui/icons/PhotoAlbum'
import PublicIcon from '../../ui/icons/Public'
import SettingsIcon from '../../ui/icons/Settings'
import WavingHandIcon from '../../ui/icons/WavingHand'
import AccessibleChip from '../AccessibleChip'
import AccessibleDialog from '../AccessibleDialog'
import CreatedAtChip from '../CreatedAtChip'

export interface SoundboardCardProps {
  mobileSoundboard?: MobileSoundboard
  frontendWebSoundboard?: FrontendWebSoundboard
  desktopSoundboard?: DesktopSoundboard
  onSetSoundboardToEdit: () => void
  onDuplicate?: (soundboardUuid: string) => void
  onExportSoundboardZip?: () => void
  onExportSoundboardJson?: () => void
  duplicating?: boolean
  onOpenSoundboard: () => void
  asAdmin?: boolean
}

export default function SoundboardCard({
  mobileSoundboard,
  frontendWebSoundboard,
  desktopSoundboard,
  onSetSoundboardToEdit,
  onDuplicate,
  onExportSoundboardZip,
  onExportSoundboardJson,
  duplicating,
  onOpenSoundboard,
  asAdmin,
}: SoundboardCardProps) {
  const mergedSoundboard = {
    ...frontendWebSoundboard,
    ...mobileSoundboard,
    ...desktopSoundboard,
  }

  const userEmail = mergedSoundboard.user?.email
  const {
    uuid: soundboardUuid,
    createdAt,
    name,
    public: isPublic,
    requestFeatured,
    featured,
    description,
  } = mergedSoundboard

  const allPads = [
    ...(frontendWebSoundboard?.pads || []),
    ...(mobileSoundboard?.pads || []),
    ...(desktopSoundboard?.freeformKeys || []),
    ...Object.values(desktopSoundboard?.keys || {}).filter((k) =>
      Boolean(k.trackname),
    ),
  ]
  const numberOfPads = allPads.length

  const [open, setOpen] = useState(false)

  if (!soundboardUuid) return null

  return (
    <>
      <AccessibleDialog
        open={open}
        onClose={() => !duplicating && setOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-label={'Duplicate this soundboard?'}
      >
        <DialogTitle>Duplicate this soundboard?</DialogTitle>

        <DialogContent>
          <Typography variant="body1">
            A copy of this soundboard will be made with the same name and same
            settings.
          </Typography>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            loading={duplicating}
            onClick={() => {
              onDuplicate && onDuplicate(soundboardUuid)
              setOpen(false)
            }}
            variant="contained"
          >
            Duplicate
          </LoadingButton>
          <LoadingButton disabled={duplicating} onClick={() => setOpen(false)}>
            Cancel
          </LoadingButton>
        </DialogActions>
      </AccessibleDialog>

      <Paper className="h-full p-4" elevation={4}>
        <div className="flex h-full flex-col items-stretch gap-2">
          <div>
            <Typography
              variant="body1"
              style={{ fontSize: '1.2rem' }}
              data-soundboard-uuid={soundboardUuid}
            >
              {name || <i>None</i>}
            </Typography>

            {asAdmin && userEmail && (
              <Typography variant="body2" color="text.secondary">
                {userEmail}
              </Typography>
            )}
          </div>

          <div className="flex flex-row flex-wrap gap-2">
            {createdAt && <CreatedAtChip createdAt={createdAt} />}

            {numberOfPads > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${numberOfPads} Sounds`}
              />
            )}

            {numberOfPads === 0 && (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={'No Sounds'}
              />
            )}

            {isPublic && (
              <Chip
                size="small"
                color="success"
                label="Public"
                icon={<PublicIcon fontSize="small" />}
              />
            )}

            {requestFeatured && (
              <Chip
                size="small"
                label="Publish request"
                color="warning"
                icon={<WavingHandIcon fontSize="small" />}
              />
            )}

            {featured && (
              <Chip
                size="small"
                label="Published"
                color="success"
                icon={<PhotoAlbumIcon fontSize="small" />}
              />
            )}
          </div>

          {description && (
            <div className="max-h-[70px] overflow-y-scroll">
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ whiteSpace: 'pre' }}
              >
                {description}
              </Typography>
            </div>
          )}

          <div className="flex w-full flex-row flex-wrap items-stretch justify-start gap-2">
            <AccessibleChip
              onClick={() => onOpenSoundboard()}
              size="small"
              label="Open"
              color="info"
              icon={<OpenInNewIcon fontSize="small" />}
            />

            {onDuplicate && (
              <AccessibleChip
                onClick={() => setOpen(true)}
                size="small"
                label="Clone"
                icon={<ContentCopyIcon fontSize="small" />}
                color="info"
              />
            )}

            <AccessibleChip
              onClick={() => soundboardUuid && onSetSoundboardToEdit()}
              size="small"
              icon={<SettingsIcon fontSize="small" />}
              label="Settings"
            />

            {onExportSoundboardZip && (
              <Tooltip title="Export as ZIP (sounds and config)">
                <AccessibleChip
                  size="small"
                  clickable
                  onClick={() =>
                    onExportSoundboardZip && onExportSoundboardZip()
                  }
                  label="Export ZIP"
                  color="primary"
                  icon={<FolderZipIcon fontSize="small" />}
                />
              </Tooltip>
            )}

            {onExportSoundboardJson && (
              <Tooltip title="Export as JSON (no sounds)">
                <AccessibleChip
                  size="small"
                  clickable
                  onClick={() =>
                    onExportSoundboardJson && onExportSoundboardJson()
                  }
                  label="Export JSON"
                  color="primary"
                  icon={<DataObjectIcon fontSize="small" />}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </Paper>
    </>
  )
}
